// src/components/pages/chat/BotFlow.jsx
"use client";

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuthToken } from "../../../utils/authStorage";
import { API_BASE_URL as API_BASE } from "../../../config/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";

export default function BotFlowDashboard() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const isRestrictedRole = ["superadmin", "admin", "reseller", "company"].includes(user?.role);

  useEffect(() => {
    if (isRestrictedRole) {
      navigate(user?.role === "reseller" ? "/dashreseller" : (user?.role === "superadmin" ? "/su-admin" : "/dash-admin"), { replace: true });
    }
  }, [isRestrictedRole, user?.role, navigate]);

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = getAuthToken();
  const headers = token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };

  const loadFlows = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/bot-flows`, { headers });
      const data = await res.json();
      if (data.success) {
        setFlows(data.flows || []);
      }
    } catch (e) {
      console.error("Failed to load flows", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlows();
  }, []);

  if (user && !user.bot) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Feature Not Accessible</h3>
        <p className="text-gray-600 dark:text-gray-400 max-w-md">
          Please contact your administrator to enable the Stateful Bot Flow feature on your account.
        </p>
      </div>
    );
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this flow?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/bot-flows/${id}`, {
        method: "DELETE",
        headers,
      });
      const data = await res.json();
      if (data.success) {
        setFlows((prev) => prev.filter((f) => f._id !== id));
      } else {
        alert("Failed to delete flow: " + data.message);
      }
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 md:p-8 transition-colors duration-500 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="mx-auto max-w-7xl">
        
        {/* Header Section */}
        <div className="mb-10 text-center md:text-left flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500 mb-3 tracking-tight">
              Visual Flow Builder
            </h1>
            <p className={`text-lg max-w-2xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Design complex, multi-step conversation flows with an intuitive drag-and-drop canvas.
            </p>
          </div>
          <button
            onClick={() => navigate('/botflow/new')}
            className="flex items-center gap-2 relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-500/30 transition-all duration-300 hover:shadow-blue-500/50 hover:-translate-y-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
            Create New Flow
          </button>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className={`animate-pulse h-48 rounded-[2rem] ${isDark ? 'bg-gray-800/50' : 'bg-white/50'}`}></div>
            ))}
          </div>
        ) : flows.length === 0 ? (
          <div className={`flex flex-col items-center justify-center h-64 rounded-[2rem] border-2 border-dashed ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-300 bg-white/50'}`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
            </div>
            <p className={`text-lg font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No flows found</p>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Create your first visual flow to automate conversations.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {flows.map((flow, idx) => (
              <div 
                key={flow._id} 
                className={`group relative rounded-[2rem] p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${isDark ? 'bg-gray-800 border border-gray-700/50 hover:shadow-blue-900/20 hover:border-blue-500/30' : 'bg-white shadow-sm border border-transparent hover:shadow-blue-500/10 hover:border-blue-100'}`}
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 pr-4">
                    <h3 className={`text-xl font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} title={flow.name}>
                      {flow.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${flow.isActive ? (isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${flow.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                        {flow.isActive ? 'Active' : 'Paused'}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                        {flow.steps?.length || 0} Nodes
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl space-y-2 mb-6 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                  <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Trigger Phrase</p>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {flow.startTriggerType?.replace('_', ' ')} <span className="font-mono bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded shadow-sm">"{flow.startTriggerText}"</span>
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Link 
                    to={`/botflow/${flow._id}`}
                    className={`flex-1 text-center py-2.5 rounded-xl text-sm font-bold transition-colors ${isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-50 hover:bg-blue-100 text-blue-700'}`}
                  >
                    Open Canvas
                  </Link>
                  <button 
                    onClick={() => handleDelete(flow._id)}
                    className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${isDark ? 'bg-gray-700 hover:bg-red-900/50 text-red-400 hover:text-red-300' : 'bg-gray-100 hover:bg-red-50 text-red-600'}`}
                  >
                    Delete
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
